/**
 * FauxDS AccordionItem - Recreated from Figma MCP
 *
 * ⚠️ This is a SIMPLIFIED RECREATION of the Slate AccordionItem component for prototyping.
 * It is NOT the real @faire/slate AccordionItem and should NOT be used for:
 * - Engineering handoff
 * - Design specs
 * - Production code
 *
 * For the real Slate AccordionItem, see: @faire/slate/components/accordion
 *
 * This component was recreated from Figma MCP design specs:
 * - Figma node: 51246:20229 (Slate Accordion Item)
 * - Design tokens extracted from Figma variables
 * - All variants verified: Expanded/Collapsed, Default/Hover/Active states
 * - Content types verified: None, Description, Custom Slot
 * - Typography: Label (18px), Description (14px), Custom Slot (12px)
 */
import { cn } from "@faire/prototype-playground/lib/utils";
import React, { forwardRef, useState, useCallback } from "react";

export type AccordionItemState = "Default" | "Hover" | "Active";
export type AccordionItemContentType = "None" | "Description" | "Custom Slot";

export interface AccordionItemProps {
  /**
   * The label/title text of the accordion item
   */
  label: string;
  /**
   * The description text (shown when contentType is "Description")
   */
  descriptionText?: string;
  /**
   * Custom content slot (shown when contentType is "Custom Slot")
   */
  contentSlot?: React.ReactNode | null;
  /**
   * Whether the accordion item is expanded
   * @default false
   */
  expanded?: boolean;
  /**
   * Visual state of the accordion item
   * @default "Default"
   */
  state?: AccordionItemState;
  /**
   * Type of content to display when expanded
   * @default "None"
   */
  contentType?: AccordionItemContentType;
  /**
   * Whether the accordion item is focused (for keyboard navigation)
   * @default false
   */
  focused?: boolean;
  /**
   * Accessibility label for screen readers
   * @default labelText
   */
  accessibilityLabel?: string;
  /**
   * Called when the accordion item is toggled
   */
  onToggle?: (expanded: boolean) => void;
  /**
   * **playground only**
   * Optional className for additional styling
   */
  className?: string;
}

// Chevron Down Icon (16px × 16px)
const ChevronDownIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="shrink-0"
  >
    <path
      d="M4 6L8 10L12 6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Chevron Up Icon (16px × 16px)
const ChevronUpIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="shrink-0"
  >
    <path
      d="M4 10L8 6L12 10"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * AccordionItem - FauxDS (recreated from Figma MCP)
 *
 * An Accordion is a list of sectioned content that can be expanded or collapsed.
 * Sections are usually organized by a title and description and the expand/collapse
 * interaction is visualized by a directional icon.
 *
 * This implementation was created by extracting design specs directly from Figma.
 */
const AccordionItem = forwardRef<HTMLButtonElement, AccordionItemProps>(
  (
    {
      label: labelText,
      descriptionText = "Description",
      contentSlot = null,
      expanded: controlledExpanded,
      state = "Default",
      contentType = "None",
      focused = false,
      accessibilityLabel,
      onToggle,
      className = "",
    },
    ref
  ) => {
    const [internalExpanded, setInternalExpanded] = useState(false);

    // Use controlled state if provided, otherwise use internal state
    const isControlled = controlledExpanded !== undefined;
    const expanded = isControlled ? controlledExpanded : internalExpanded;

    const handleToggle = useCallback(() => {
      if (isControlled) {
        onToggle?.(!expanded);
      } else {
        setInternalExpanded(!expanded);
        onToggle?.(!expanded);
      }
    }, [isControlled, expanded, onToggle]);

    // Determine visual state styles based on Figma design tokens
    const getStateStyles = () => {
      switch (state) {
        case "Hover":
          return {
            labelText: "text-[var(--slate-color-action-text-hover)]", // #000000
            bg: "bg-[var(--slate-color-action-surface-inverse-hover)]", // #f7f7f7
          };
        case "Active":
          return {
            labelText: "text-[var(--slate-color-action-text-default)]", // #333333
            bg: "bg-[var(--slate-color-action-surface-inverse-hover)]", // #f7f7f7
          };
        case "Default":
        default:
          return {
            labelText: "text-[var(--slate-color-action-text-default)]", // #333333
            bg: "bg-transparent",
          };
      }
    };

    const stateStyles = getStateStyles();
    const ariaLabel = accessibilityLabel || labelText;

    return (
      <div
        className={cn(
          "border-b border-[var(--slate-color-action-border-subdued)]",
          className
        )}
      >
        {/* Header/Button */}
        <button
          ref={ref}
          type="button"
          onClick={handleToggle}
          aria-expanded={expanded}
          aria-label={ariaLabel}
          className={cn(
            "flex w-full cursor-pointer items-center justify-between border-none bg-transparent px-0 py-[16px] text-left transition-colors duration-150 ease-in-out",
            stateStyles.bg,
            stateStyles.labelText,
            focused
              ? "outline outline-2 outline-offset-2 outline-[var(--slate-color-action-border-focus-ring)]"
              : "",
            "focus:outline-hidden focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--slate-color-action-border-focus-ring)]"
          )}
        >
          {/* Label - Using Figma typography tokens for Subheading */}
          <span
            className="flex-1"
            style={{
              fontFamily: "var(--slate-typography-family-sans-serif)",
              fontSize: "var(--slate-typography-font-size-300)",
              fontWeight: "var(--slate-typography-weight-regular)",
              lineHeight: "var(--slate-typography-line-height-300)",
              letterSpacing: "var(--slate-typography-letter-spacing-200)",
            }}
          >
            {labelText}
          </span>

          {/* Chevron Icon */}
          <span
            className="ml-[16px] flex items-center justify-center"
            style={{
              width: "var(--slate-dimensions-4)",
              height: "var(--slate-dimensions-4)",
              color: "var(--slate-color-action-icon-default)",
            }}
          >
            {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </span>
        </button>

        {/* Content — always in DOM so height transition is smooth (avoids layout jump) */}
        {(contentType === "Description" || contentType === "Custom Slot") && (
          <div
            className={cn(
              "overflow-hidden transition-all duration-300 ease-in-out",
              expanded ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
            )}
          >
            <div className="pb-[16px]">
              {contentType === "Description" && descriptionText && (
                <p
                  style={{
                    fontFamily: "var(--slate-typography-family-sans-serif)",
                    fontSize: "var(--slate-typography-font-size-200)",
                    fontWeight: "var(--slate-typography-weight-regular)",
                    lineHeight: "var(--slate-typography-line-height-200)",
                    letterSpacing: "var(--slate-typography-letter-spacing-200)",
                    color: "var(--slate-color-action-text-default)",
                    whiteSpace: "pre-line",
                  }}
                >
                  {descriptionText}
                </p>
              )}

              {contentType === "Custom Slot" && (
                <div
                  className="flex items-center justify-center rounded-lg border border-dashed p-4"
                  style={{
                    borderColor: "#a171ff", // Custom Slot Stroke
                    backgroundColor: "#a171ff33", // Custom Slot Fill (with opacity)
                  }}
                >
                  {contentSlot ? (
                    contentSlot
                  ) : (
                    <p
                      style={{
                        fontFamily: "var(--slate-typography-family-sans-serif)",
                        fontSize: "var(--slate-typography-font-size-100)",
                        fontWeight: "var(--slate-typography-weight-regular)",
                        lineHeight: "var(--slate-typography-line-height-100)",
                        letterSpacing:
                          "var(--slate-typography-letter-spacing-200)",
                        color: "#a171ff", // Custom Slot Text
                      }}
                    >
                      Custom slot
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);

AccordionItem.displayName = "AccordionItem";

export default AccordionItem;
